import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  getStoredTrustedToken,
  hashTrustedToken,
  clearStoredTrustedToken,
} from "@/lib/trustedDevice";

export function useSellerOtpStatus() {
  const { user } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["seller", "otp-status", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: seller, error } = await supabase
        .from("sellers")
        .select("is_otp_verified")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;

      const isOtpVerified = !!seller?.is_otp_verified;

      // Verify trusted device on the server. The hashed token + JWT must
      // both match for the device to count as trusted.
      let deviceTrusted = false;
      const stored = getStoredTrustedToken();
      if (stored) {
        try {
          const hash = await hashTrustedToken(stored);
          const { data: ok } = await supabase.rpc("consume_trusted_device", {
            p_token_hash: hash,
          });
          deviceTrusted = !!ok;
          if (!deviceTrusted) clearStoredTrustedToken();
        } catch {
          deviceTrusted = false;
        }
      }

      return { isOtpVerified, deviceTrusted };
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  return {
    isOtpVerified: data?.isOtpVerified ?? false,
    deviceTrusted: data?.deviceTrusted ?? false,
    // Seller may access seller pages only when they've completed setup AND
    // the current device is trusted (or they just completed verification —
    // setStoredTrustedToken happens before this refetch).
    canAccessSellerArea: !!(data?.isOtpVerified && data?.deviceTrusted),
    loading: isLoading,
    refetch,
  };
}
